import React from "react";

/*
 * InteractivePerspectiveRectangle — true 4-corner perspective editor used
 * by the admin's "Ecrã da tablet" preview.
 *
 * Each of the 4 corner handles is a FREE 2D point: dragging it in any
 * direction (including diagonals) updates ONLY that corner's offset in
 * container-%. The visible overlay is a polygon drawn with SVG that
 * follows the 4 handles, letting the admin skew the frame into a
 * trapezoid that matches the perspective of the tablet in the mascot
 * image.
 *
 * Data model:
 *   x, y, w, h — baseline rectangle in container-% (unchanged, used for
 *     sliders and nudges).
 *   corners = {
 *     tl: { dx, dy }, tr: { dx, dy },
 *     br: { dx, dy }, bl: { dx, dy },
 *   } — per-corner offsets in container-%. All zero → the polygon is a
 *     perfect rectangle.
 *
 * onChange:
 *   onChange({ x, y, w, h }) when the baseline rect moves (center drag).
 *   onCornerChange(cornerKey, { dx, dy }) when a corner handle moves.
 */
export function InteractivePerspectiveRectangle({
  x, y, w, h,
  corners,
  onChange,
  onCornerChange,
  rotate = 0,
  radius = 6,
  bezel = 0,
  bezelColor = "#1a1a1a",
  shadow = 0,
  imgRef,
}) {
  const dragRef = React.useRef(null);

  const containerBox = () => {
    const el = imgRef?.current;
    if (!el) return null;
    return el.getBoundingClientRect();
  };

  // Target points (container-%) for each corner — natural + offset.
  const tl = { x: x + (corners.tl?.dx || 0), y: y + (corners.tl?.dy || 0) };
  const tr = { x: x + w + (corners.tr?.dx || 0), y: y + (corners.tr?.dy || 0) };
  const br = { x: x + w + (corners.br?.dx || 0), y: y + h + (corners.br?.dy || 0) };
  const bl = { x: x + (corners.bl?.dx || 0), y: y + h + (corners.bl?.dy || 0) };

  const beginMove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const box = containerBox();
    if (!box) return;
    dragRef.current = {
      mode: "move",
      startPx: { x: e.clientX, y: e.clientY },
      startRect: { x, y, w, h },
      box,
    };
    e.target.setPointerCapture?.(e.pointerId);
    const onMove = (ev) => {
      const dr = dragRef.current;
      if (!dr) return;
      const dxPct = ((ev.clientX - dr.startPx.x) / dr.box.width) * 100;
      const dyPct = ((ev.clientY - dr.startPx.y) / dr.box.height) * 100;
      const nx = clamp(dr.startRect.x + dxPct, -40, 100 - (dr.startRect.w - 40));
      const ny = clamp(dr.startRect.y + dyPct, -40, 100 - (dr.startRect.h - 40));
      onChange({ x: round1(nx), y: round1(ny), w: dr.startRect.w, h: dr.startRect.h });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const beginCorner = (key) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const box = containerBox();
    if (!box) return;
    const startOffset = { dx: corners[key]?.dx || 0, dy: corners[key]?.dy || 0 };
    dragRef.current = {
      mode: "corner",
      key,
      startPx: { x: e.clientX, y: e.clientY },
      startOffset,
      box,
    };
    e.target.setPointerCapture?.(e.pointerId);
    const onMove = (ev) => {
      const dr = dragRef.current;
      if (!dr) return;
      const dxPct = ((ev.clientX - dr.startPx.x) / dr.box.width) * 100;
      const dyPct = ((ev.clientY - dr.startPx.y) / dr.box.height) * 100;
      onCornerChange(dr.key, {
        dx: round1(dr.startOffset.dx + dxPct),
        dy: round1(dr.startOffset.dy + dyPct),
      });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const handleBase = {
    position: "absolute",
    width: 16,
    height: 16,
    background: "#0ea5e9",
    border: "2px solid #ffffff",
    borderRadius: "50%",
    boxShadow: "0 1px 3px rgba(0,0,0,0.6)",
    touchAction: "none",
    zIndex: 6,
    marginLeft: -8,
    marginTop: -8,
  };

  // Centroid for the central "move everything" handle.
  const centroid = {
    x: (tl.x + tr.x + br.x + bl.x) / 4,
    y: (tl.y + tr.y + br.y + bl.y) / 4,
  };

  return (
    <>
      {/* SVG polygon so the shape can be any quadrilateral — not a bounding box. */}
      <svg
        className="absolute inset-0 pointer-events-none"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ width: "100%", height: "100%", zIndex: 3 }}
      >
        <polygon
          points={`${tl.x},${tl.y} ${tr.x},${tr.y} ${br.x},${br.y} ${bl.x},${bl.y}`}
          fill="rgba(14,165,233,0.22)"
          stroke="#06b6d4"
          strokeWidth="0.35"
          strokeLinejoin="round"
        />
      </svg>

      {/* Centre handle — move the whole polygon by translating the base rect. */}
      <div
        onPointerDown={beginMove}
        className="absolute cursor-move"
        style={{
          left: `${centroid.x}%`,
          top: `${centroid.y}%`,
          width: 22,
          height: 22,
          marginLeft: -11,
          marginTop: -11,
          borderRadius: "50%",
          background: "rgba(14,165,233,0.9)",
          border: "2px solid white",
          boxShadow: "0 1px 3px rgba(0,0,0,0.5)",
          touchAction: "none",
          zIndex: 5,
        }}
        data-testid="tablet-rect-centre"
      />

      {/* 4 corner handles — each 100% independent (free 2D movement). */}
      {[
        { key: "tl", pt: tl },
        { key: "tr", pt: tr },
        { key: "br", pt: br },
        { key: "bl", pt: bl },
      ].map(({ key, pt }) => (
        <div
          key={key}
          onPointerDown={beginCorner(key)}
          data-testid={`tablet-rect-handle-${key}`}
          style={{
            ...handleBase,
            left: `${pt.x}%`,
            top: `${pt.y}%`,
            cursor: "move",
          }}
        />
      ))}
    </>
  );
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const round1 = (v) => Math.round(v * 10) / 10;

/*
 * NudgeControls — 4 arrow buttons + reset for 0.5 % fine-tuning of the
 * rectangle position/size, independent from pointer dragging.
 */
export function NudgeControls({ x, y, w, h, onChange }) {
  const step = 0.5;
  const bump = (key, delta) => () => {
    const next = { x, y, w, h, [key]: Math.max(0, Math.min(100, (({ x, y, w, h })[key]) + delta)) };
    if (next.x + next.w > 100) next.x = 100 - next.w;
    if (next.y + next.h > 100) next.y = 100 - next.h;
    onChange({ x: round1(next.x), y: round1(next.y), w: round1(next.w), h: round1(next.h) });
  };
  const Btn = ({ label, ...rest }) => (
    <button
      type="button"
      className="px-2 py-1 text-[11px] rounded border border-slate-300 bg-white hover:bg-slate-100"
      {...rest}
    >
      {label}
    </button>
  );
  return (
    <div className="flex flex-wrap gap-2 items-center text-[11px] text-slate-600" data-testid="tablet-nudge">
      <span className="font-semibold">Ajuste fino (±{step}%):</span>
      <div className="flex gap-1">
        <Btn label="◀ X-" onClick={bump("x", -step)} data-testid="nudge-x-neg" />
        <Btn label="X+ ▶" onClick={bump("x", step)} data-testid="nudge-x-pos" />
      </div>
      <div className="flex gap-1">
        <Btn label="▲ Y-" onClick={bump("y", -step)} data-testid="nudge-y-neg" />
        <Btn label="Y+ ▼" onClick={bump("y", step)} data-testid="nudge-y-pos" />
      </div>
      <div className="flex gap-1">
        <Btn label="◀ W-" onClick={bump("w", -step)} data-testid="nudge-w-neg" />
        <Btn label="W+ ▶" onClick={bump("w", step)} data-testid="nudge-w-pos" />
      </div>
      <div className="flex gap-1">
        <Btn label="▲ H-" onClick={bump("h", -step)} data-testid="nudge-h-neg" />
        <Btn label="H+ ▼" onClick={bump("h", step)} data-testid="nudge-h-pos" />
      </div>
    </div>
  );
}
